import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Edit,
  Eye,
  Share,
  Loader,
  Copy,
  ExternalLink,
  Trash2,
  ChevronDown,
  Calendar,
  Download,
  FileText,
} from 'lucide-react';
import { useFlowStore } from '../stores/flowStore';
import { AnalyticsDashboard } from '../components/AnalyticsCard';
import {
  DraftStatusBadge,
  DraftDetailButton,
} from '../components/DraftStatusIndicator';
import { useToast } from '../hooks/useToast';
import { getFormUrl, getEventFormUrl, appConfig } from '../config/appConfig';
import { eventApi } from '../services/api';
import { Event } from '@dynamic-flow/types';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useDownloadFormResponseReport } from '@/hooks/useDownloadFormResponseReport';
import { useThrottle } from '@/hooks/useThrottle';

export function Dashboard() {
  const { toast } = useToast();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'flows' | 'events'>('flows');
  const [events, setEvents] = useState<Event[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    flows,
    isLoading,
    error,
    loadFlows,
    createNewFlow,
    publishFlow,
    unpublishFlow,
    deleteFlow,
  } = useFlowStore();

  useDeepCompareEffect(() => {
    // Only load flows if they haven't been loaded yet
    loadFlows();
  }, [loadFlows]);

  const downloadFormResponseMutation = useDownloadFormResponseReport();

  const loadEvents = async () => {
    setEventsLoading(true);
    setEventsError(null);
    try {
      const response = await eventApi.getAll();
      setEvents(response.events || []);
    } catch (error) {
      console.error('Failed to load events:', error);
      setEventsError('Failed to load events');
    } finally {
      setEventsLoading(false);
    }
  };

  // Load events when switching to events tab
  useEffect(() => {
    if (
      activeTab === 'events' &&
      events.length === 0 &&
      !eventsLoading &&
      !eventsError
    ) {
      loadEvents();
    }
  }, [activeTab, events.length, eventsLoading]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCreateFlow = async () => {
    try {
      // Create blank flow only
      const newFlow = await createNewFlow('Untitled Flow');

      // Navigate to builder with the new flow ID
      if (newFlow?.id) {
        window.location.href = `/builder/${newFlow.id}`;
      }
    } catch (error) {
      console.error('Failed to create flow:', error);
    }
  };

  const handleCreateEvent = () => {
    // Navigate to event builder
    window.location.href = '/builder/events';
  };

  const handleDeleteEvent = async (eventId: string, eventName: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${eventName}"?\n\nThis action cannot be undone.`
    );

    if (confirmed) {
      try {
        await eventApi.delete(eventId);
        setEvents(events.filter((event) => event.eventId !== eventId));
        toast.success('Event deleted successfully');
      } catch (error) {
        console.error('Failed to delete event:', error);
        toast.error('Failed to delete event');
      }
    }
  };

  const handlePublishFlow = async (flowId: string) => {
    try {
      await publishFlow(flowId);
      toast.success('Flow published successfully');
      // Refresh analytics after publishing if analytics feature is enabled
      if (appConfig.features.analytics) {
        // Trigger analytics refresh by reloading flows
        loadFlows();
      }
    } catch (error) {
      console.error('Failed to publish flow:', error);
      toast.error('Failed to publish flow');
    }
  };

  const handleUnpublishFlow = async (flowId: string) => {
    try {
      await unpublishFlow(flowId);
      toast.success('Flow unpublished successfully');
      // Refresh analytics after unpublishing if analytics feature is enabled
      if (appConfig.features.analytics) {
        // Trigger analytics refresh by reloading flows
        loadFlows();
      }
    } catch (error) {
      console.error('Failed to unpublish flow:', error);
      toast.error('Failed to unpublish flow');
    }
  };

  const handleDeleteFlow = async (flowId: string, flowTitle: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${flowTitle}"?\n\nThis action cannot be undone.`
    );

    if (confirmed) {
      try {
        await deleteFlow(flowId);
        toast.success('Flow deleted successfully');
        // Refresh analytics after deleting if analytics feature is enabled
        if (appConfig.features.analytics) {
          // Trigger analytics refresh by reloading flows
          loadFlows();
        }
      } catch (error) {
        console.error('Failed to delete flow:', error);
        toast.error('Failed to delete flow');
      }
    }
  };

  const handleDownloadFlowResponseReport = useThrottle(
    async (flowId: string) => {
      if (downloadFormResponseMutation.isPending) {
        return;
      }
      await downloadFormResponseMutation.mutateAsync(flowId);
    },
    500
  );

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString();
  };

  const getPublicFormUrl = (flowId: string) => {
    return getFormUrl(flowId, 'public');
  };

  const copyFormUrl = async (flowId: string) => {
    const url = getPublicFormUrl(flowId);

    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL Copied!', 'Form URL has been copied to clipboard.');
    } catch (error) {
      console.error('Failed to copy URL:', error);
      toast.error('Copy Failed', 'Could not copy URL to clipboard.');
    }
  };

  const openPublicForm = (flowId: string) => {
    const url = getPublicFormUrl(flowId);
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

        {/* Create Dropdown Menu */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50"
            disabled={isLoading}
          >
            <Plus className="w-4 h-4" />
            {isLoading ? 'Creating...' : 'Create New'}
            <ChevronDown className="w-4 h-4" />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
              <button
                onClick={() => {
                  handleCreateFlow();
                  setIsDropdownOpen(false);
                }}
                className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                disabled={isLoading}
              >
                <Plus className="w-4 h-4 text-gray-500" />
                Create New Flow
              </button>
              <button
                onClick={() => {
                  handleCreateEvent();
                  setIsDropdownOpen(false);
                }}
                className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-3"
              >
                <Calendar className="w-4 h-4 text-gray-500" />
                Create New Event
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Analytics Dashboard */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white p-6 rounded-lg shadow-sm border animate-pulse"
            >
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      ) : (
        <AnalyticsDashboard flows={flows} />
      )}

      <div className="bg-white rounded-lg shadow-sm border">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('flows')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'flows'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Recent Forms
              </div>
            </button>
            <button
              onClick={() => setActiveTab('events')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'events'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Recent Events
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'flows' && (
          <>
            {/* Error State */}
            {error && (
              <div className="p-6 text-center">
                <div className="text-red-600 mb-2">⚠️ Error loading flows</div>
                <p className="text-gray-500 text-sm">{error}</p>
                <button
                  onClick={() => {
                    loadFlows();
                    // Refresh analytics if feature is enabled
                    if (appConfig.features.analytics) {
                      // Force reload to refresh analytics
                      setTimeout(() => loadFlows(), 100);
                    }
                  }}
                  className="mt-2 text-blue-600 hover:text-blue-700 text-sm"
                >
                  Try again
                </button>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="p-6 text-center">
                <Loader className="w-8 h-8 animate-spin mx-auto text-blue-600" />
                <p className="text-gray-500 text-sm mt-2">Loading flows...</p>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && flows.length === 0 && (
              <div className="p-6 text-center">
                <div className="text-gray-400 mb-2">📝</div>
                <p className="text-gray-500 text-sm">No flows created yet</p>
                <button
                  onClick={handleCreateFlow}
                  className="mt-2 inline-block text-blue-600 hover:text-blue-700 text-sm"
                >
                  Create your first flow
                </button>
              </div>
            )}

            {/* Flows List */}
            {!isLoading && !error && flows.length > 0 && (
              <div className="divide-y">
                {flows.map((flow) => (
                  <div key={flow.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900">
                          {flow.title || 'Untitled Flow'}
                        </h3>
                        <p className="text-gray-500 text-sm mt-1">
                          {flow.description || 'No description'}
                        </p>
                        <div className="flex items-center space-x-4 mt-2">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              flow.status === 'published'
                                ? 'bg-green-100 text-green-800'
                                : flow.status === 'draft'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {flow.status === 'draft' ? 'Draft' : flow.status}
                          </span>
                          <DraftStatusBadge
                            currentFlow={flow}
                            publishedFlow={flow.publishedContent}
                            size="small"
                          />
                          <span className="text-sm text-gray-500">
                            {flow.responsesCount} responses
                          </span>
                          <span className="text-sm text-gray-500">
                            Created {formatDate(flow.createdAt)}
                          </span>
                          {flow.updatedAt &&
                            flow.updatedAt !== flow.createdAt && (
                              <span className="text-sm text-gray-500">
                                Modified {formatDate(flow.updatedAt)}
                              </span>
                            )}
                        </div>

                        {/* Published form URL */}
                        {flow.status === 'published' && (
                          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-green-800 mb-1">
                                  Public Form URL:
                                </p>
                                <code className="text-sm text-green-700 bg-green-100 px-2 py-1 rounded break-all">
                                  {getPublicFormUrl(flow.id)}
                                </code>
                              </div>
                              <div className="flex items-center space-x-1 ml-4">
                                <button
                                  onClick={() => copyFormUrl(flow.id)}
                                  className="p-2 text-green-600 hover:text-green-700 hover:bg-green-100 rounded"
                                  title="Copy URL"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => openPublicForm(flow.id)}
                                  className="p-2 text-green-600 hover:text-green-700 hover:bg-green-100 rounded"
                                  title="Open form in new tab"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Draft Changes Detail */}
                        {flow.status === 'published' && (
                          <div className="mt-3">
                            <DraftDetailButton
                              flowId={flow.id}
                              publishedFlow={flow.publishedContent}
                              size="small"
                            />
                          </div>
                        )}
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <Link
                          to={`/builder/${flow.id}`}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="Edit Draft"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() =>
                            window.open(
                              getFormUrl(flow.id, 'preview'),
                              '_blank'
                            )
                          }
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="Preview Draft"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {flow.status === 'published' && (
                          <button
                            onClick={() =>
                              window.open(
                                getFormUrl(flow.id, 'public'),
                                '_blank'
                              )
                            }
                            className="p-2 text-green-400 hover:text-green-600 hover:bg-green-100 rounded"
                            title="View Published Form"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        )}

                        {/* Publish/Unpublish Button */}
                        {flow.status === 'published' ? (
                          <button
                            onClick={() => handleUnpublishFlow(flow.id)}
                            className="p-2 rounded text-red-400 hover:text-red-600 hover:bg-red-100"
                            title="Unpublish"
                          >
                            <Share className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handlePublishFlow(flow.id)}
                            className="p-2 rounded text-blue-400 hover:text-blue-600 hover:bg-blue-100"
                            title="Publish Draft"
                          >
                            <Share className="w-4 h-4" />
                          </button>
                        )}

                        <button
                          onClick={() =>
                            handleDownloadFlowResponseReport(flow.id)
                          }
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="Delete Flow"
                          disabled={downloadFormResponseMutation.isPending}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {/* Delete Button */}
                        <button
                          onClick={() => handleDeleteFlow(flow.id, flow.title)}
                          className="p-2 rounded text-red-400 hover:text-red-600 hover:bg-red-100"
                          title="Delete Flow"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Events Tab Content */}
        {activeTab === 'events' && (
          <>
            {/* Events Error State */}
            {eventsError && (
              <div className="p-6 text-center">
                <div className="text-red-600 mb-2">⚠️ Error loading events</div>
                <p className="text-gray-500 text-sm">{eventsError}</p>
                <button
                  onClick={loadEvents}
                  className="mt-2 text-blue-600 hover:text-blue-700 text-sm"
                >
                  Try again
                </button>
              </div>
            )}

            {/* Events Loading State */}
            {eventsLoading && (
              <div className="p-6 text-center">
                <Loader className="w-8 h-8 animate-spin mx-auto text-blue-600" />
                <p className="text-gray-500 text-sm mt-2">Loading events...</p>
              </div>
            )}

            {/* Events Empty State */}
            {!eventsLoading && !eventsError && events.length === 0 && (
              <div className="p-6 text-center">
                <div className="text-gray-400 mb-2">📅</div>
                <p className="text-gray-500 text-sm">No events created yet</p>
                <button
                  onClick={handleCreateEvent}
                  className="mt-2 inline-block text-blue-600 hover:text-blue-700 text-sm"
                >
                  Create your first event
                </button>
              </div>
            )}

            {/* Events List */}
            {!eventsLoading && !eventsError && events.length > 0 && (
              <div className="divide-y">
                {events.map((event) => {
                  const feedbackFormUrl = event.feedbackUrl
                    ? getEventFormUrl(event.feedbackUrl, event.eventId ?? '')
                    : '';
                  const registrationFormUrl = event.registrationUrl
                    ? getEventFormUrl(
                        event.registrationUrl,
                        event.eventId ?? ''
                      )
                    : '';

                  return (
                  <div key={event.eventId} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900">
                          {event.name || 'Untitled Event'}
                        </h3>
                        <p className="text-gray-500 text-sm mt-1">
                          {event.description || 'No description'}
                        </p>
                        <div className="flex items-center space-x-4 mt-2">
                          <span className="text-sm text-gray-500">
                            📍 {event.location}
                          </span>
                          <span className="text-sm text-gray-500">
                            📅 {formatDate(event.startDateTime)} -{' '}
                            {formatDate(event.endDateTime)}
                          </span>
                        </div>

                        {/* Event URLs */}
                        {(event.feedbackUrl || event.registrationUrl) && (
                          <div className="mt-3 space-y-2">
                            {event.feedbackUrl && (
                              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-blue-800 mb-1">
                                      Feedback Form:
                                    </p>
                                    <code className="text-sm text-blue-700 bg-blue-100 px-2 py-1 rounded break-all">
                                      {feedbackFormUrl}
                                    </code>
                                  </div>
                                  <div className="flex items-center space-x-1 ml-4">
                                    <button
                                      onClick={() =>
                                        navigator.clipboard.writeText(
                                          feedbackFormUrl
                                        )
                                      }
                                      className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded"
                                      title="Copy URL"
                                    >
                                      <Copy className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() =>
                                        window.open(feedbackFormUrl, '_blank')
                                      }
                                      className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded"
                                      title="Open form in new tab"
                                    >
                                      <ExternalLink className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}

                            {event.registrationUrl && (
                              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-green-800 mb-1">
                                      Registration Form:
                                    </p>
                                    <code className="text-sm text-green-700 bg-green-100 px-2 py-1 rounded break-all">
                                      {registrationFormUrl}
                                    </code>
                                  </div>
                                  <div className="flex items-center space-x-1 ml-4">
                                    <button
                                      onClick={() =>
                                        navigator.clipboard.writeText(
                                          registrationFormUrl
                                        )
                                      }
                                      className="p-2 text-green-600 hover:text-green-700 hover:bg-green-100 rounded"
                                      title="Copy URL"
                                    >
                                      <Copy className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() =>
                                        window.open(
                                          registrationFormUrl,
                                          '_blank'
                                        )
                                      }
                                      className="p-2 text-green-600 hover:text-green-700 hover:bg-green-100 rounded"
                                      title="Open form in new tab"
                                    >
                                      <ExternalLink className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() =>
                            (window.location.href = `/builder/events/${event.eventId}`)
                          }
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="Edit Event"
                        >
                          <Edit className="w-4 h-4" />
                        </button>

                        {/* Delete Button */}
                        <button
                          onClick={() =>
                            handleDeleteEvent(event.eventId || '', event.name)
                          }
                          className="p-2 rounded text-red-400 hover:text-red-600 hover:bg-red-100"
                          title="Delete Event"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
